import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Alert, AlertDescription, AlertIcon } from '../../design-system/alert';
import { useUserCountMetadata } from '../../queries/users';
import { GroupType } from '../../schemas/groups';
import { Conceal } from '../display/conceal';
import { Reveal } from '../display/reveal';

export type EligibilityCountCondition = { attr: string | undefined; value: string | undefined };

type EligibilityCountProps = {
	conditions: EligibilityCountCondition[];
	operator: GroupType;
	translationNamespace?: string;
	translationKeyPrefix?: string;
	/** Use a different key for the count message (e.g. "audienceCount_nominator" for nominator section). Default "audienceCount". */
	countMessageKey?: string;
};

export const EligibilityCount: FC<EligibilityCountProps> = ({
	conditions,
	operator,
	translationNamespace = 'awards',
	translationKeyPrefix = 'form',
	countMessageKey = 'audienceCount',
}) => {
	const { t } = useTranslation(translationNamespace, { keyPrefix: translationKeyPrefix });

	const filteredConditions = useMemo(() => {
		return conditions.filter((condition) => condition.attr && condition.value) as { attr: string; value: string }[];
	}, [conditions]);

	const hasIncompleteCondition = conditions.some((c) => c.attr && !c.value);

	const { data } = useUserCountMetadata({ conditions: filteredConditions, operator, status: 'active' });

	// User picked an attribute but did not select a value
	if (hasIncompleteCondition) {
		return (
			<Alert status="warn" size="sm" mb={4}>
				<AlertIcon />
				<AlertDescription>{t(`${countMessageKey}_scoped_zero`)}</AlertDescription>
			</Alert>
		);
	}

	if (!data) {
		return null;
	}

	const context = data.scopedCount === data.totalCount ? 'unscoped' : 'scoped';

	return (
		<>
			<Conceal if={data.scopedCount === 0}>
				<Alert status="info" size="sm" mb={4}>
					<AlertIcon />
					<AlertDescription>{t(countMessageKey, { count: data.scopedCount, context })}</AlertDescription>
				</Alert>
			</Conceal>
			<Reveal if={data.scopedCount === 0}>
				<Alert status="error" size="sm" mb={4}>
					<AlertIcon />
					<AlertDescription>{t(countMessageKey, { count: data.scopedCount, context })}</AlertDescription>
				</Alert>
			</Reveal>
		</>
	);
};
